import { createContext, useContext, useState, useEffect } from "react";

const AppIdContext = createContext(null);

export const AppIdProvider = ({ children }) => {
  // Auth related state
  const [appId, setAppId] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  const [total_voters, setTotalVoters] = useState(null);
  const [language, setLanguage] = useState('en');
  const [userId, setUserId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Printer related state
  const [savedPrinter, setSavedPrinter] = useState(null);
  
  // Vidhan Sabha related state
  const [vidhanSabhaNo, setVidhanSabhaNo] = useState(null);
  const [vidhanSabhaName, setVidhanSabhaName] = useState(null);
  
  // Candidate related state
  const [candidateNo, setCandidateNo] = useState(null);
  const [candidateName, setCandidateName] = useState(null);
  
  // Party related state
  const [partyShortName, setPartyShortName] = useState(null);
  const [partyFullName, setPartyFullName] = useState(null);
  const [partyLogoPng, setPartyLogoPng] = useState(null);
  const [partyLogoJpg, setPartyLogoJpg] = useState(null);
  
  // Slip messages
  const [slipMessage, setSlipMessage] = useState(null);
  const [smsSlipMessage, setSmsSlipMessage] = useState(null);
  
  // Offline settings
  const [offlineStatus, setOfflineStatus] = useState(null);
  const [offlineDbUrl, setOfflineDbUrl] = useState(null);
  const [offlineVer, setOfflineVer] = useState(null);
  
  // App settings
  const [splaceUrl, setSplaceUrl] = useState(null);
  const [appLink, setAppLink] = useState(null);
  const [videoLink, setVideoLink] = useState(null);
  const [appVer, setAppVer] = useState(null);
  
  // User type and booth
  const [userType, setUserType] = useState(null);
  const [boothNo, setBoothNo] = useState(null);
  const [tempStatus, setTempStatus] = useState(null);
  
  // Popup settings
  const [popupStatus, setPopupStatus] = useState(null);
  const [popupUrl, setPopupUrl] = useState(null);

  // Load auth state from localStorage on initial load
  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedAppId = localStorage.getItem('appId');
        const storedUserName = localStorage.getItem('userName');
        const storedUserId = localStorage.getItem('userId');
        const storedTotalVoters = localStorage.getItem('totalVoters');
        const storedLanguage = localStorage.getItem('language');
        const storedUserPhoto = localStorage.getItem('userPhoto');

        if (storedAppId && storedUserName && storedUserId) {
          setAppId(storedAppId);
          setUserName(storedUserName);
          setUserId(storedUserId);
          setTotalVoters(storedTotalVoters ? parseInt(storedTotalVoters) : null);
          setLanguage(storedLanguage || 'en');
          setUserPhoto(storedUserPhoto || null);
          setIsAuthenticated(true);
          
          // Load additional user data
          const storedVidhanSabhaNo = localStorage.getItem('vidhanSabhaNo');
          const storedVidhanSabhaName = localStorage.getItem('vidhanSabhaName');
          const storedCandidateNo = localStorage.getItem('candidateNo');
          const storedCandidateName = localStorage.getItem('candidateName');
          const storedPartyShortName = localStorage.getItem('partyShortName');
          const storedPartyFullName = localStorage.getItem('partyFullName');
          const storedPartyLogoPng = localStorage.getItem('partyLogoPng');
          const storedPartyLogoJpg = localStorage.getItem('partyLogoJpg');
          const storedSlipMessage = localStorage.getItem('slipMessage');
          const storedSmsSlipMessage = localStorage.getItem('smsSlipMessage');
          const storedOfflineStatus = localStorage.getItem('offlineStatus');
          const storedOfflineDbUrl = localStorage.getItem('offlineDbUrl');
          const storedOfflineVer = localStorage.getItem('offlineVer');
          const storedSplaceUrl = localStorage.getItem('splaceUrl');
          const storedAppLink = localStorage.getItem('appLink');
          const storedVideoLink = localStorage.getItem('videoLink');
          const storedAppVer = localStorage.getItem('appVer');
          const storedUserType = localStorage.getItem('userType');
          const storedBoothNo = localStorage.getItem('boothNo');
          const storedTempStatus = localStorage.getItem('tempStatus');
          const storedPopupStatus = localStorage.getItem('popupStatus');
          const storedPopupUrl = localStorage.getItem('popupUrl');
          
          if (storedVidhanSabhaNo) setVidhanSabhaNo(storedVidhanSabhaNo);
          if (storedVidhanSabhaName) setVidhanSabhaName(storedVidhanSabhaName);
          if (storedCandidateNo) setCandidateNo(storedCandidateNo);
          if (storedCandidateName) setCandidateName(storedCandidateName);
          if (storedPartyShortName) setPartyShortName(storedPartyShortName);
          if (storedPartyFullName) setPartyFullName(storedPartyFullName);
          if (storedPartyLogoPng) setPartyLogoPng(storedPartyLogoPng);
          if (storedPartyLogoJpg) setPartyLogoJpg(storedPartyLogoJpg);
          if (storedSlipMessage) setSlipMessage(storedSlipMessage);
          if (storedSmsSlipMessage) setSmsSlipMessage(storedSmsSlipMessage);
          if (storedOfflineStatus) setOfflineStatus(storedOfflineStatus);
          if (storedOfflineDbUrl) setOfflineDbUrl(storedOfflineDbUrl);
          if (storedOfflineVer) setOfflineVer(storedOfflineVer);
          if (storedSplaceUrl) setSplaceUrl(storedSplaceUrl);
          if (storedAppLink) setAppLink(storedAppLink);
          if (storedVideoLink) setVideoLink(storedVideoLink);
          if (storedAppVer) setAppVer(storedAppVer);
          if (storedUserType) setUserType(storedUserType);
          if (storedBoothNo) setBoothNo(storedBoothNo);
          if (storedTempStatus) setTempStatus(storedTempStatus === 'true');
          if (storedPopupStatus) setPopupStatus(storedPopupStatus);
          if (storedPopupUrl) setPopupUrl(storedPopupUrl);
        }
      } catch (error) {
        console.error("Error loading auth state:", error);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Load saved printer from localStorage on initial load
  useEffect(() => {
    const storedPrinter = localStorage.getItem('savedPrinter');
    if (storedPrinter) {
      try {
        const parsedPrinter = JSON.parse(storedPrinter);
        setSavedPrinter(parsedPrinter);
      } catch (e) {
        console.error("Error parsing saved printer:", e);
      }
    }
  }, []);

  // Save auth state to localStorage whenever it changes
  useEffect(() => {
    if (appId && userName && userId) {
      localStorage.setItem('appId', appId);
      localStorage.setItem('userName', userName);
      localStorage.setItem('userId', userId);
      if (total_voters) localStorage.setItem('totalVoters', total_voters);
      if (language) localStorage.setItem('language', language);
      if (userPhoto) localStorage.setItem('userPhoto', userPhoto);
    }
  }, [appId, userName, userId, total_voters, language, userPhoto]);

  // Save additional user data to localStorage
  useEffect(() => {
    if (vidhanSabhaNo) localStorage.setItem('vidhanSabhaNo', vidhanSabhaNo);
    if (vidhanSabhaName) localStorage.setItem('vidhanSabhaName', vidhanSabhaName);
    if (candidateNo) localStorage.setItem('candidateNo', candidateNo);
    if (candidateName) localStorage.setItem('candidateName', candidateName);
    if (partyShortName) localStorage.setItem('partyShortName', partyShortName);
    if (partyFullName) localStorage.setItem('partyFullName', partyFullName);
    if (partyLogoPng) localStorage.setItem('partyLogoPng', partyLogoPng);
    if (partyLogoJpg) localStorage.setItem('partyLogoJpg', partyLogoJpg);
    if (slipMessage) localStorage.setItem('slipMessage', slipMessage);
    if (smsSlipMessage) localStorage.setItem('smsSlipMessage', smsSlipMessage);
    if (offlineStatus) localStorage.setItem('offlineStatus', offlineStatus);
    if (offlineDbUrl) localStorage.setItem('offlineDbUrl', offlineDbUrl);
    if (offlineVer) localStorage.setItem('offlineVer', offlineVer);
    if (splaceUrl) localStorage.setItem('splaceUrl', splaceUrl);
    if (appLink) localStorage.setItem('appLink', appLink);
    if (videoLink) localStorage.setItem('videoLink', videoLink);
    if (appVer) localStorage.setItem('appVer', appVer);
    if (userType) localStorage.setItem('userType', userType);
    if (boothNo) localStorage.setItem('boothNo', boothNo);
    if (tempStatus !== null) localStorage.setItem('tempStatus', tempStatus);
    if (popupStatus) localStorage.setItem('popupStatus', popupStatus);
    if (popupUrl) localStorage.setItem('popupUrl', popupUrl);
  }, [
    vidhanSabhaNo, vidhanSabhaName, candidateNo, candidateName,
    partyShortName, partyFullName, partyLogoPng, partyLogoJpg,
    slipMessage, smsSlipMessage, offlineStatus, offlineDbUrl,
    offlineVer, splaceUrl, appLink, videoLink, appVer,
    userType, boothNo, tempStatus, popupStatus, popupUrl
  ]);

  // Save printer to localStorage whenever it changes
  useEffect(() => {
    if (savedPrinter) {
      localStorage.setItem('savedPrinter', JSON.stringify(savedPrinter));
    } else {
      localStorage.removeItem('savedPrinter');
    }
  }, [savedPrinter]);

  // Functions for printer management
  const setSavedPrinterData = (printerData) => {
    setSavedPrinter(printerData);
  };

  const clearSavedPrinter = () => {
    setSavedPrinter(null);
  };

  // Clear localStorage on logout
  const logout = () => {
    // Clear auth state
    setAppId(null);
    setUserName(null);
    setUserId(null);
    setTotalVoters(null);
    setUserPhoto(null);
    setLanguage('en');
    setIsAuthenticated(false);
    
    // Clear printer
    setSavedPrinter(null);
    
    // Clear additional user data
    setVidhanSabhaNo(null);
    setVidhanSabhaName(null);
    setCandidateNo(null);
    setCandidateName(null);
    setPartyShortName(null);
    setPartyFullName(null);
    setPartyLogoPng(null);
    setPartyLogoJpg(null);
    setSlipMessage(null);
    setSmsSlipMessage(null);
    setOfflineStatus(null);
    setOfflineDbUrl(null);
    setOfflineVer(null);
    setSplaceUrl(null);
    setAppLink(null);
    setVideoLink(null);
    setAppVer(null);
    setUserType(null);
    setBoothNo(null);
    setTempStatus(null);
    setPopupStatus(null);
    setPopupUrl(null);

    // Remove all items from localStorage
    localStorage.removeItem('appId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
    localStorage.removeItem('totalVoters');
    localStorage.removeItem('language');
    localStorage.removeItem('userPhoto');
    localStorage.removeItem('userVidhanSabha');
    localStorage.removeItem('savedPrinter');
    
    // Remove additional items
    localStorage.removeItem('vidhanSabhaNo');
    localStorage.removeItem('vidhanSabhaName');
    localStorage.removeItem('candidateNo');
    localStorage.removeItem('candidateName');
    localStorage.removeItem('partyShortName');
    localStorage.removeItem('partyFullName');
    localStorage.removeItem('partyLogoPng');
    localStorage.removeItem('partyLogoJpg');
    localStorage.removeItem('slipMessage');
    localStorage.removeItem('smsSlipMessage');
    localStorage.removeItem('offlineStatus');
    localStorage.removeItem('offlineDbUrl');
    localStorage.removeItem('offlineVer');
    localStorage.removeItem('splaceUrl');
    localStorage.removeItem('appLink');
    localStorage.removeItem('videoLink');
    localStorage.removeItem('appVer');
    localStorage.removeItem('userType');
    localStorage.removeItem('boothNo');
    localStorage.removeItem('tempStatus');
    localStorage.removeItem('popupStatus');
    localStorage.removeItem('popupUrl');
    localStorage.removeItem('userData');
  };

  return (
    <AppIdContext.Provider
      value={{
        // Auth related
        appId,
        setAppId,
        userName,
        setUserName,
        userPhoto,
        setUserPhoto,
        language,
        setLanguage,
        userId,
        setUserId,
        total_voters,
        setTotalVoters,
        isAuthenticated,
        setIsAuthenticated,
        loading,
        logout,
        
        // Printer related
        savedPrinter,
        setSavedPrinter: setSavedPrinterData,
        clearSavedPrinter,
        
        // Vidhan Sabha related
        vidhanSabhaNo,
        setVidhanSabhaNo,
        vidhanSabhaName,
        setVidhanSabhaName,
        
        // Candidate related
        candidateNo,
        setCandidateNo,
        candidateName,
        setCandidateName,
        
        // Party related
        partyShortName,
        setPartyShortName,
        partyFullName,
        setPartyFullName,
        partyLogoPng,
        setPartyLogoPng,
        partyLogoJpg,
        setPartyLogoJpg,
        
        // Slip messages
        slipMessage,
        setSlipMessage,
        smsSlipMessage,
        setSmsSlipMessage,
        
        // Offline settings
        offlineStatus,
        setOfflineStatus,
        offlineDbUrl,
        setOfflineDbUrl,
        offlineVer,
        setOfflineVer,
        
        // App settings
        splaceUrl,
        setSplaceUrl,
        appLink,
        setAppLink,
        videoLink,
        setVideoLink,
        appVer,
        setAppVer,
        
        // User type and booth
        userType,
        setUserType,
        boothNo,
        setBoothNo,
        tempStatus,
        setTempStatus,
        
        // Popup settings
        popupStatus,
        setPopupStatus,
        popupUrl,
        setPopupUrl,
      }}
    >
      {children}
    </AppIdContext.Provider>
  );
};

export const useAppId = () => useContext(AppIdContext);